import { useMutation } from "@tanstack/react-query";
import { useSaveAndExitAtomValue } from "apps/commons/atoms/saveAndExitAtom";
import { reporter } from "apps/commons/functions/reporter";
import { useDynamicLocation } from "apps/commons/hooks/useDynamicLocation";
import { UseUrlBuilder } from "apps/commons/hooks/useUrlBuilder";
import { useSetSidebarAtom } from "apps/oneOnboarding/sidebar/sidebarAtom";
import { useFormSaveResultHook } from "apps/oneOnboarding/useFormSaveResultHook";
import { oneOnboardingApi } from "libs/api";
import type { ProfileData } from "libs/codegen/property-onboarding";
import { useParams } from "router";
import type { BulkOnboardSession } from "../BulkOnboardSession";

export const useBulkProfileFormHandler = () => {
    const { pathname } = useDynamicLocation();
    const { lang } = useParams("/:lang/list-my-property/onboard/batch/profile");
    const saveAndExitAction = useSaveAndExitAtomValue();
    const setSidebarAtom = useSetSidebarAtom();

    const { onSuccess, onError } = useFormSaveResultHook();

    return useMutation({
        mutationFn: async (data: ProfileData) => {
            const onboardSessionStorage = sessionStorage.getItem("bulkOnboardingSession");

            if (onboardSessionStorage === null) throw new Error("Session data not found");

            const onboardSession: BulkOnboardSession = JSON.parse(onboardSessionStorage);

            return oneOnboardingApi
                .saveBulkOnboardingProfileData(lang, {
                    profileData: data,
                    sessionId: onboardSession.sessionId,
                    bulkPropertyKeys: onboardSession.properties.map((property) => ({
                        propertyId: Number(property.propertyId),
                        onboardingId: property.onboardingId,
                    })),
                })
                .then((res) => res.data);
        },
        onSuccess: (saveResult) =>
            onSuccess(saveResult, {
                onSuccess: ({ nextUrl, sidebarItemList }) => {
                    if (sidebarItemList && sidebarItemList.length > 0) setSidebarAtom(sidebarItemList);

                    reporter.info(
                        `Successful at useBulkProfileFormHandler
    operation: saveBulkOnboardingProfileData
    path: ${pathname}
    nextAction: ${saveAndExitAction ? "saveAndExit" : "redirect"}
    to: ${saveAndExitAction ? new UseUrlBuilder(lang).propertySearchUrl() : nextUrl}`,
                    );
                },
                onError: (error) => {
                    reporter.error(
                        `Error at useBulkProfileFormHandler
    operation: saveBulkOnboardingProfileData
    path: ${pathname}
    scope: onError
    message: ${error?.message}
    stack: ${error?.stack}`,
                    );
                },
            }),
        onError: (error: Error) =>
            onError(error, {
                onError: (error) => {
                    reporter.error(
                        `Error at useBulkProfileFormHandler
    operation: saveBulkOnboardingProfileData
    path: ${pathname}
    scope: onError
    message: ${error?.message}
    stack: ${error?.stack}`,
                    );
                },
            }),
    });
};
